<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm animate-modalFade">
    <!-- Container Modal -->
    <div class="w-full m-3 max-w-4xl bg-gray-800 text-gray-300 rounded-lg shadow-lg overflow-hidden border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
        <div class="flex items-center gap-3">
          <i class="fas fa-book-open text-indigo-300 text-2xl"></i>
          <h2 class="text-lg sm:text-xl font-semibold text-indigo-300">รายการทักษะที่เรียนรู้</h2>
        </div>
        <button
          @click="closeModal"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200"
          :disabled="loading"
        >
          <i class="fas fa-times text-base"></i>
        </button>
      </div>

      <!-- Filters -->
      <div v-if="!loading" class="space-y-2">
        <div class="bg-gray-800 rounded-md p-2 border border-gray-700 grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-2">
          <button
            v-for="filter in filtersWithCount"
            :key="filter.value"
            @click="selectedFilter = filter.value"
            :class="[
              'flex items-center justify-center gap-2 text-xs sm:text-sm py-2 rounded-md transition-colors duration-200 font-medium',
              selectedFilter === filter.value
                ? 'bg-indigo-600 text-white border border-indigo-700'
                : 'bg-gray-700 text-indigo-200 hover:bg-gray-600 border border-gray-600'
            ]"
          >
            <i :class="filter.icon" class="text-xs sm:text-sm"></i>
            <span class="ml-1 px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-xs">{{ filter.count }}</span>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
      </div>

      <!-- Skills grid -->
      <div
        v-if="!loading && filteredSkills.length > 0"
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-72 overflow-y-auto pr-2"
        ref="skillList"
        id="style-1"
      >
        <div
          v-for="(skill, index) in filteredSkills"
          :key="skill.name"
          :class="[
            'bg-gray-800 rounded-md p-3 flex flex-col items-center border transition-all duration-200 relative w-full',
            learnedSkills.includes(skill.name) ? 'border-indigo-600 ring-1 ring-indigo-700' : 'border-gray-700',
            'hover:bg-gray-700 hover:border-indigo-600'
          ]"
          :data-skill="skill.name"
        >
          <img
            v-if="skill.img"
            :src="skill.img"
            alt="ภาพหนังสือ"
            class="w-20 h-20 object-cover rounded-md mb-2 border border-gray-700"
            @error="skill.img = fallbackImg"
          />
          <img
            v-else
            :src="fallbackImg"
            alt="placeholder"
            class="w-20 h-20 object-cover rounded-md mb-2 border border-gray-700"
          />
          <p class="text-xs sm:text-sm font-semibold text-center leading-snug"
             :class="learnedSkills.includes(skill.name) ? 'text-white' : 'text-indigo-200'">
            {{ (index + 1) + '. ' + skill.name }}
          </p>
          <p class="text-xs text-indigo-200 text-center mt-1 leading-snug">
            {{ statLine(skill.stats) }}
          </p>

          <span
            v-if="learnedSkills.includes(skill.name)"
            class="absolute top-2 right-2 text-green-400"
          >
            <i class="fas fa-check-circle text-sm"></i>
          </span>
        </div>
      </div>

      <!-- Empty states -->
      <div v-if="!loading && filteredSkills.length === 0" class="text-indigo-200 text-sm text-center py-6">
        ไม่มีทักษะในหมวดหมู่นี้
      </div>
      <div v-if="!loading && learnedSkills.length === 0" class="text-indigo-200 text-sm text-center py-6">
        คุณยังไม่ได้เรียนรู้ทักษะใดๆ
      </div>

      <!-- Error -->
      <div v-if="errorMessage" class="text-red-300 text-sm text-center py-3">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { books } from '@/constants/books';
import gameItemsData from '@/data/gameItems.json';

export default {
  name: 'SkillChecklistModal',
  props: {
    isOpen: { type: Boolean, default: false }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const playerStore = usePlayerStore();

    const availableSkills = ref([]);
    const learnedSkills = computed(() => playerStore.characterPrivateData?.learnedSkills || []);
    const loading = ref(true);
    const errorMessage = ref('');
    const selectedFilter = ref('stamina');
    const skillList = ref(null);

    const fallbackImg = 'https://via.placeholder.com/80?text=Book';

    const filters = ref([
      { value: 'stamina', label: 'Stamina', short: 'Sta', icon: 'fas fa-heartbeat' },
      { value: 'str',     label: 'STR',     short: 'STR', icon: 'fas fa-dumbbell' },
      { value: 'agi',     label: 'AGI',     short: 'AGI', icon: 'fas fa-running' },
      { value: 'int',     label: 'INT',     short: 'INT', icon: 'fas fa-brain' },
      { value: 'luck',    label: 'Luck',    short: 'Lck', icon: 'fas fa-dice' },
      { value: 'allstats',  label: 'allstats',  short: 'allstats', icon: 'fas fa-person-dots-from-line' },
      { value: 'craft',   label: 'Craft',   short: 'Crf', icon: 'fas fa-tools' },
      { value: 'ghost',   label: 'ghost',   short: 'Ghost', icon: 'fas fa-ghost' },
      { value: 'normal',  label: 'ทั่วไป',  short: 'ทั่วไป', icon: 'fas fa-book' },
    ]);

    // Enrich filters with live counts
    const filtersWithCount = computed(() => {
      return filters.value.map(f => ({
        ...f,
        count: availableSkills.value.filter(s => s.type === f.value).length
      }));
    });

    // Compose stat line
    const statLine = (stats = {}) => {
      try {
        return Object.keys(stats)
          .filter(k => Number(stats[k]) > 0)
          .map(k => `+${stats[k]} ${k.toUpperCase()}`)
          .join(', ');
      } catch {
        return '';
      }
    };

    // Data fetchers
    const fetchGameItems = () => {
      try {
        const gameItemsMap = new Map();
        Object.entries(gameItemsData).forEach(([name, data]) => {
          gameItemsMap.set(name, {
            img: data.img || '',
            stats: data.stats || {}
          });
        });

        availableSkills.value = books.map(skill => {
          const gi = gameItemsMap.get(skill.name);
          return {
            name: skill.name,
            type: skill.type,
            stats: gi ? gi.stats : skill.stats,
            img: gi ? gi.img : ''
          };
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching gameItems:', err);
        errorMessage.value = 'ไม่สามารถโหลดข้อมูลหนังสือได้ ใช้ข้อมูล fallback แทน';
        availableSkills.value = books.map(skill => ({
          name: skill.name,
          type: skill.type,
          stats: skill.stats,
          img: ''
        }));
      }
    };

    // Scroll to first learned
    const scrollToFirstSkill = () => {
      if (!skillList.value || !learnedSkills.value.length) return;
      nextTick(() => {
        const first = skillList.value.querySelector('[data-skill="' + learnedSkills.value[0] + '"]');
        if (first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    };

    // Derived list
    const filteredSkills = computed(() => {
      return availableSkills.value.filter(s => s.type === selectedFilter.value);
    });

    const closeModal = () => {
      if (!loading.value) {
        selectedFilter.value = 'stamina';
        errorMessage.value = '';
        emit('close');
      }
    };

    // React to open
    watch(() => props.isOpen, (isOpen) => {
      if (isOpen) {
        loading.value = true;
        fetchGameItems();
        loading.value = false;
        scrollToFirstSkill();
      }
    });

    onMounted(() => {
      if (props.isOpen) {
        fetchGameItems();
        loading.value = false;
        scrollToFirstSkill();
      }
    });

    return {
      // state
      availableSkills,
      learnedSkills,
      loading,
      errorMessage,
      selectedFilter,
      filtersWithCount,
      filteredSkills,
      skillList,
      fallbackImg,
      // methods
      closeModal,
      statLine
    };
  }
};
</script>

<style scoped>
/* Animations (Tailwind v2-compatible custom) */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

/* Scrollbar */
#style-1::-webkit-scrollbar { width: 6px; }
#style-1::-webkit-scrollbar-track { background: #111827; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #4b5563; }
#style-1 { scrollbar-width: thin; scrollbar-color: #374151 #111827; }

/* Buttons */
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }

/* Max height helper (for Tailwind v2) */
.max-h-96 { max-height: 24rem; }
</style>